import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormControl, Validators } from "@angular/forms";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { MatDialog, MatDialogConfig } from "@angular/material";
import { Observable, Subject } from "rxjs";
import { switchMap, takeUntil } from "rxjs/operators";

import { ConfirmDialogComponent } from "../../core/confirm-dialog/confirm-dialog.component";
import { UserService } from "../shared/user.service";
import { SnackBarService } from "../../core/snack-bar.service";
import { ValidationHelperService } from "../../core/validation-helper.service";

import { TemporaryPassword } from "../shared/temporary-password.model";
import { User } from "../shared/user.model";
import { UserOptions } from "../shared/user-options.model";

@Component({
    template: require('./user-detail.component.html'),
    styles: [require('./user-detail.component.css')]
})
export class UserDetailComponent implements OnInit {
    loading: boolean;
    loadingSave: boolean;
    user: User;
    userForm: FormGroup;
    userOpts: UserOptions;

    private ngUnsubscribe: Subject<void> = new Subject(); //see https://stackoverflow.com/a/41177163

    constructor(
        private activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private fb: FormBuilder,
        private userService: UserService,
        private router: Router,
        private snackBarService: SnackBarService,
        private validationHelperService: ValidationHelperService) {

        this.userForm = this.fb.group({
            username: '',
            nameTitleCode: '',
            firstName: ['', [Validators.required, Validators.maxLength(60)]],
            middleName: ['', Validators.maxLength(60)],
            lastName: ['', [Validators.required, Validators.maxLength(60)]],
            emailAddress: ['', [Validators.required, Validators.maxLength(100)]],
            phoneNumber: ['', Validators.maxLength(20)],
            mobileNumber: ['', Validators.maxLength(20)],
            jobTitle: ['', Validators.maxLength(250)],
            isWebAdmin: false,
            isActive: false,
            isLocked: false,
            temporaryPassword: ''
        });
    }

    ngOnInit() {
        this.loading = true;

        this.userService.getOptions()
            .then(opts => {
                this.userOpts = opts;

                this.activatedRoute.paramMap
                    .pipe(
                        switchMap((params: ParamMap) => this.userService.getUser(+params.get("id"))), // (+) converts string "id" to a number
                        takeUntil(this.ngUnsubscribe)
                    )
                    .subscribe(user => {
                        this.loading = false;
                        this.user = user;
                        this.userForm.patchValue(user);
                        this.userForm.get("username").disable();
                        this.temporaryPasswordFC.disable();
                    },
                    err => {
                        this.loading = false;
                        this.snackBarService.openSnackBar("Error retrieving user.");
                    });
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    get temporaryPasswordFC(): FormControl { return this.userForm.get("temporaryPassword") as FormControl; }

    actionsDisabled(): boolean {
        return this.showLoadingSpinner()
            || this.showSaveSpinner();
    }

    goToUsersList(): void {
        this.router.navigate(["/users"]);
    }

    prepareSaveUser(): User {
        const formModel = this.userForm.value;
        // note - username field will be disabled, currently ignored by the backend anyway
        const username = this.userForm.get("username").value as string;

        const saveUser: User = {
            id: this.user.id,
            username: username,
            nameTitleCode: formModel.nameTitleCode as string,
            firstName: formModel.firstName as string,
            middleName: formModel.middleName as string,
            lastName: formModel.lastName as string,
            emailAddress: formModel.emailAddress as string,
            phoneNumber: formModel.phoneNumber as string,
            mobileNumber: formModel.mobileNumber as string,
            jobTitle: formModel.jobTitle as string,
            isWebAdmin: formModel.isWebAdmin as boolean,
            isActive: formModel.isActive as boolean,
            isLocked: formModel.isLocked as boolean,
        };

        return saveUser;
    }

    resetPassword(): void {
        let dialogRef = this.dialog.open(ConfirmDialogComponent);
        dialogRef.componentInstance.question = "Reset password?";
        dialogRef.componentInstance.confirmButtonLabel = "RESET";

        dialogRef.afterClosed().subscribe(userConfirmed => {
            if (userConfirmed) {
                this.loadingSave = true;

                this.userService.resetPassword(this.user.id)
                    .then(res => {
                        this.loadingSave = false;
                        
                        this.temporaryPasswordFC.setValue(res.temporaryPassword);
                        this.temporaryPasswordFC.disable();

                        this.snackBarService.openSnackBar("Password has been reset.");
                    })
                    .catch(err => {
                        this.loadingSave = false;
                        this.snackBarService.openSnackBar(err);
                    });
            }
        });
    }

    save(): void {
        if (this.userForm.invalid) {
            this.validationHelperService.markAllTouched(this.userForm);
            this.snackBarService.openSnackBar("Please fill in required values.");
            return;
        }

        this.loadingSave = true;
        const userToSave = this.prepareSaveUser();

        this.userService.update(userToSave)
            .then(() => {
                this.loadingSave = false;
                this.snackBarService.openSnackBar("User updated.");
                this.goToUsersList();
            })
            .catch(err => {
                this.loadingSave = false;
                this.snackBarService.openSnackBar(err);
            });
    }

    showLoadingSpinner(): boolean {
        return this.loading;
    }

    showSaveSpinner(): boolean {
        return this.loadingSave;
    }

    showTemporaryPassword(): boolean {
        return this.temporaryPasswordFC.value;
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./src/app/admin/user-detail/user-detail.component.ts